import React, { useMemo } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { SButton } from "@ui/components/buttons/SButton";
import { DCard } from "@ui/components/card/DCard";
import { colCenter } from "@ui/components/style/style";
import SignalMeter from "@ui/components/icons/SignalMeter";
import StatusIndicator from "@ui/components/status/StatusIndicator";
import { Icon } from "@iconify/react";
import { SMenu } from "@ui/components/menu/SMenu";
import { SIconButton } from "@ui/components/buttons";
import { GridCellStack } from "@ui/components/grid";
import { useTranslation } from "react-i18next";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
export const APDetailsCard = ({ name = "Access Point", deviceId = "", apId, status = "active", lastUpdatedAt, address, place, calibrationDate, onSettingsClick, }) => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    /** One-shot attention when SignalR patches `lastUpdatedAt` (primary tint + scale + glow). */
    const lastUpdatedAttention = useMemo(() => keyframes({
        "0%": {
            backgroundColor: "transparent",
            boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}`,
            transform: "scale(1)",
        },
        "22%": {
            backgroundColor: alpha(theme.palette.primary.main, 0.26),
            boxShadow: `0 0 0 5px ${alpha(theme.palette.primary.main, 0.2)}`,
            transform: "scale(1.07)",
        },
        "50%": {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            boxShadow: `0 0 14px 3px ${alpha(theme.palette.primary.main, 0.18)}`,
            transform: "scale(1.03)",
        },
        "100%": {
            backgroundColor: "transparent",
            boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}`,
            transform: "scale(1)",
        },
    }), [theme]);
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const [lastUpdatedHighlight, setLastUpdatedHighlight] = React.useState(false);
    const prevLastUpdatedRef = React.useRef(undefined);
    React.useEffect(() => {
        if (lastUpdatedAt === undefined) {
            prevLastUpdatedRef.current = lastUpdatedAt;
            return;
        }
        if (prevLastUpdatedRef.current !== undefined &&
            prevLastUpdatedRef.current !== lastUpdatedAt) {
            setLastUpdatedHighlight(true);
            const timer = window.setTimeout(() => setLastUpdatedHighlight(false), 1100);
            prevLastUpdatedRef.current = lastUpdatedAt;
            return () => window.clearTimeout(timer);
        }
        prevLastUpdatedRef.current = lastUpdatedAt;
    }, [lastUpdatedAt]);
    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const handleRequestMaintenance = () => {
        navigate(`/dashboard/${apId}/request-maintenance`);
        handleMenuClose();
    };
    const handleReport = () => {
        navigate(`/dashboard/${apId}/reports`);
        handleMenuClose();
    };
    const handleSettings = () => {
        if (onSettingsClick) {
            onSettingsClick();
        }
        handleMenuClose();
    };
    const menuItems = [
        {
            label: t("actions.requestMaintenance"),
            onClick: handleRequestMaintenance,
        },
        {
            label: t("actions.report"),
            onClick: handleReport,
        },
        ...(onSettingsClick
            ? [
                {
                    label: t("actions.settings"),
                    onClick: handleSettings,
                },
            ]
            : []),
    ];
    const actionItems = [
        {
            label: t("actions.requestMaintenance"),
            onClick: handleRequestMaintenance,
            variant: "soft",
        },
        {
            label: t("actions.report"),
            onClick: handleReport,
            variant: "soft",
            color: "primary",
        },
        ...(onSettingsClick
            ? [
                {
                    label: t("actions.settings"),
                    onClick: handleSettings,
                    variant: "contained",
                    color: "primary",
                },
            ]
            : []),
    ];
    const lastUpdatedLine = lastUpdatedAt
        ? `${formatUtcToLocalDate(lastUpdatedAt)} ${formatUtcToLocalTime(lastUpdatedAt, false)}`.trim()
        : "";
    const calibrationLine = calibrationDate
        ? formatUtcToLocalDate(calibrationDate) || calibrationDate
        : "";
    const metaRow = (label, dateText, options) => (React.createElement(Typography, { variant: "body2", component: "div", sx: {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            columnGap: 0.5,
            rowGap: 0.25,
        } },
        React.createElement(Typography, { component: "span", variant: "body2", color: "text.secondary" },
            label,
            ":"),
        React.createElement(Box, { component: "span", sx: {
                display: "inline-block",
                verticalAlign: "baseline",
                borderRadius: 1,
                px: 0.875,
                py: 0.375,
                mx: -0.25,
                typography: "body2",
                color: "text.primary",
                animation: (options === null || options === void 0 ? void 0 : options.highlight)
                    ? `${lastUpdatedAttention} 1s cubic-bezier(0.34, 1.25, 0.64, 1) 1 forwards`
                    : "none",
            } }, dateText || t("status.noData"))));
    const metaBlock = (React.createElement(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.75 } },
        metaRow(t("labels.lastUpdated"), lastUpdatedLine, {
            highlight: lastUpdatedHighlight,
        }),
        metaRow(t("labels.calibrationDate"), calibrationLine),
        (address || place) && (React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { mt: 0.75 } }, [address, place].filter(Boolean).join(" | ")))));
    return (React.createElement(DCard, { sx: { mb: 1, py: { xs: 1.5, lg: 1 } } }, isDesktop ? (React.createElement(Grid, { container: true, spacing: 1, alignItems: "center" },
        React.createElement(Grid, { size: "auto" },
            React.createElement(SignalMeter, { variant: status === "active" ? "success" : "warning" })),
        React.createElement(Grid, { size: { sm: 4, md: 4 } },
            React.createElement(Typography, { variant: "body1" }, name),
            React.createElement(Typography, { variant: "body2", color: "textSecondary" }, deviceId),
            React.createElement(StatusIndicator, { variant: status })),
        React.createElement(Grid, { size: "grow" }, metaBlock),
        React.createElement(Grid, { size: "auto" },
            React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) }, actionItems.map((item, index) => (React.createElement(SButton, { key: index, variant: item.variant, color: item.color, onClick: item.onClick }, item.label))))))) : (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: { display: "flex", alignItems: "flex-start", gap: 1.5 } },
            React.createElement(Box, { sx: { flexShrink: 0 } },
                React.createElement(SignalMeter, { variant: status === "active" ? "success" : "warning" })),
            React.createElement(Box, { sx: { flex: 1, minWidth: 0 } },
                React.createElement(GridCellStack, { topText: name, bottomText: deviceId, topStyle: { fontWeight: 600, lineHeight: "22px" }, bottomVariant: "caption", bottomColor: "text.secondary" }),
                React.createElement(StatusIndicator, { variant: status })),
            React.createElement(SIconButton, { size: "small", colorVariant: "inherit", showBackground: false, onClick: handleMenuClick },
                React.createElement(Icon, { icon: "eva:more-vertical-fill", color: "#231F20" })),
            React.createElement(SMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), handleClose: handleMenuClose, menuItems: menuItems })),
        React.createElement(Box, { sx: { mt: 1 } }, metaBlock)))));
};
